/**
 * `/instance/organizations/new`: creating an Organisation in one flow. Its
 * details, then its adapter in the named-field form its Settings page uses
 * (docs/adr/26-adapters-the-only-entry-point-for-outside-data.md), then the way
 * into its Organisation Admin Client, where its first administrator is added
 * and made its owner.
 *
 * The Organisation exists from the first step, so leaving after it leaves one
 * whose adapter is not configured yet, which record 26 calls a normal
 * onboarding step: it is in the Organisations list, and its Settings page
 * finishes the job. For the same reason there is no going back to the details;
 * its name is changed on that page too.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
	type CreatedOrganization,
	CreateOrganizationForm,
} from "@/components/instance/create-organization-form";
import { OrganizationAdapterCard } from "@/components/organization/settings/organization-adapter-card";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/instance/organizations/new")({
	component: InstanceNewOrganizationPage,
});

type Step =
	| { step: "details" }
	| { step: "adapter"; organization: CreatedOrganization }
	| { step: "next"; organization: CreatedOrganization };

const STEPS = [
	{ step: "details", label: "Details" },
	{ step: "adapter", label: "Adapter" },
	{ step: "next", label: "Next" },
] as const satisfies readonly { step: Step["step"]; label: string }[];

function InstanceNewOrganizationPage() {
	const [current, setCurrent] = useState<Step>({ step: "details" });
	const heading = useRef<HTMLHeadingElement>(null);
	const stepIndex = STEPS.findIndex(({ step }) => step === current.step);

	// Create and Continue replace the controls that had focus, so it moves to the
	// heading, which names the step now showing.
	useEffect(() => {
		if (current.step !== "details") heading.current?.focus();
	}, [current.step]);

	return (
		<div className="flex flex-col items-center gap-4 p-4 md:p-8">
			<Card className="w-full max-w-3xl">
				<CardHeader>
					<CardTitle>
						<h2
							aria-label={`New Organisation, step ${stepIndex + 1} of ${STEPS.length}: ${STEPS[stepIndex]?.label}`}
							ref={heading}
							tabIndex={-1}
						>
							New Organisation
						</h2>
					</CardTitle>
					<CardDescription>
						Its details, then its adapter, then its first administrator, who is
						added in its own Organisation Admin Client.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<ol aria-label="Steps" className="flex flex-wrap gap-4 text-sm">
						{STEPS.map(({ step, label }, index) => (
							<li
								aria-current={step === current.step ? "step" : undefined}
								className={cn(
									"text-muted-foreground",
									step === current.step && "font-medium text-foreground"
								)}
								key={step}
							>
								{index + 1}. {label}
							</li>
						))}
					</ol>
					{current.step === "details" ? (
						<CreateOrganizationForm
							onCreated={(organization) =>
								setCurrent({ step: "adapter", organization })
							}
						/>
					) : null}
					{current.step === "next" ? (
						<NextSteps organization={current.organization} />
					) : null}
				</CardContent>
			</Card>
			{current.step === "adapter" ? (
				<>
					<OrganizationAdapterCard
						organizationId={current.organization.id}
						organizationName={current.organization.name}
					/>
					<div className="flex w-full max-w-3xl items-center justify-end gap-3">
						<p className="text-muted-foreground text-sm">
							The adapter can also be set later, on its Settings page.
						</p>
						<Button
							onClick={() =>
								setCurrent({ step: "next", organization: current.organization })
							}
							type="button"
						>
							Continue
						</Button>
					</div>
				</>
			) : null}
		</div>
	);
}

function NextSteps({ organization }: { organization: CreatedOrganization }) {
	return (
		<div className="space-y-4">
			<p>
				<strong>{organization.name}</strong> is created.
			</p>
			<p className="text-muted-foreground text-sm">
				Open its Organisation Admin Client to add its first administrator by
				email, then make them its owner. Its name and adapter are changed there
				too, on its Settings page.
			</p>
			<div className="flex flex-wrap gap-2">
				<Link
					className={buttonVariants()}
					params={{ organizationId: organization.id }}
					rel="noopener"
					target="_blank"
					to="/organization/$organizationId"
				>
					Open {organization.name}
				</Link>
				<Link
					className={buttonVariants({ variant: "outline" })}
					to="/instance/organizations"
				>
					Back to Organisations
				</Link>
			</div>
		</div>
	);
}
